import { apiClient } from '../apiClient'
import type {
  Book,
  ReadingGuide,
  ReadingGuideGame,
  ReadingGuideImage,
} from '@/types'

// ---------------------------------------------------------------------------
// Wire shapes — what the backend actually returns.
// Dates arrive as ISO strings; convert to Date at the boundary.
// ---------------------------------------------------------------------------

export interface WireReadingGuide extends Omit<
  ReadingGuide,
  'createdAt' | 'updatedAt' | 'generatedAt' | 'deletedAt' | 'games' | 'gameRows' | 'images' | 'book'
> {
  createdAt: string
  updatedAt: string
  generatedAt?: string | null
  deletedAt?: string | null
  games?: WireReadingGuideGame[]    // backend canonical (new API)
  gameRows?: WireReadingGuideGame[] // tolerate alt naming
  images?: WireReadingGuideImage[]
  // Joined Book (BE chunk 1, 2026-06-03). Already FE-shaped; passes through
  // verbatim — nested createdAt/updatedAt stay as ISO strings.
  book?: Book | null
}

interface WireReadingGuideGame extends Omit<
  ReadingGuideGame,
  'createdAt' | 'updatedAt' | 'lastPlayedAt' | 'completedAt'
> {
  createdAt: string
  updatedAt: string
  lastPlayedAt?: string | null
  completedAt?: string | null
}

interface WireReadingGuideImage extends Omit<
  ReadingGuideImage,
  'createdAt' | 'updatedAt'
> {
  createdAt: string
  updatedAt: string
}

interface ListResponse {
  guides: WireReadingGuide[]
}

// ---------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------

function isoToDate(s: string | null | undefined): Date | null {
  return s ? new Date(s) : null
}

function normalizeGuideGame(w: WireReadingGuideGame): ReadingGuideGame {
  return {
    ...w,
    createdAt: new Date(w.createdAt),
    updatedAt: new Date(w.updatedAt),
    progressDone: w.progressDone ?? null,
    progressTotal: w.progressTotal ?? null,
    lastPlayedAt: isoToDate(w.lastPlayedAt),
    completedAt: isoToDate(w.completedAt),
  }
}

function normalizeImage(w: WireReadingGuideImage): ReadingGuideImage {
  return {
    ...w,
    createdAt: new Date(w.createdAt),
    updatedAt: new Date(w.updatedAt),
  }
}

// Backend canonical: `games` carries the new ReadingGuideGame shape on the
// wire. We re-home it onto `gameRows` (canonical FE field) and leave the
// legacy `games: Game[]` empty — consumers that need Game shapes do their own
// template-aware mapping (see CanvasView.gameFromSavedGuide).
export function normalizeGuide(w: WireReadingGuide): ReadingGuide {
  const wireRows = w.gameRows ?? w.games ?? []
  const rows = wireRows.map(normalizeGuideGame)
  return {
    ...w,
    createdAt: new Date(w.createdAt),
    updatedAt: new Date(w.updatedAt),
    generatedAt: isoToDate(w.generatedAt),
    deletedAt: isoToDate(w.deletedAt),
    images: w.images?.map(normalizeImage),
    gameRows: rows,
    games: [],
    // Defaults for FE-required arrays the backend doesn't populate yet.
    // skillsPresent tracked in specs/skills_present_backend_requirements_042926.md.
    skillsPresent: w.skillsPresent ?? [],
    bookPages: w.bookPages ?? [],
    // Book join — passes through verbatim. `w.book` is already in FE shape
    // (BE handoff guarantees the wire matches `Book`); null when absent.
    book: w.book ?? null,
  }
}

// ---------------------------------------------------------------------------
// Public API — backend-backed guide CRUD
// ---------------------------------------------------------------------------

export interface FetchAllGuidesParams {
  learnerId?: string
  favorite?: boolean
  limit?: number
  offset?: number
}

export async function fetchAllGuides(
  params?: FetchAllGuidesParams,
): Promise<ReadingGuide[]> {
  const { data } = await apiClient.get<ListResponse | WireReadingGuide[]>(
    '/api/content/guides',
    { params },
  )
  const wire = Array.isArray(data) ? data : data.guides
  return wire.map(normalizeGuide)
}

// Fetch one guide by id. 404s (including soft-deleted guides per spec §1)
// surface to TanStack Query as `error` state; consumers' optional-chaining
// against `savedGuide?` handles the not-found case gracefully.
export async function fetchGuideById(id: string): Promise<ReadingGuide> {
  const { data } = await apiClient.get<WireReadingGuide>(
    `/api/content/guides/${id}`,
  )
  return normalizeGuide(data)
}

export interface CreateGuideInput {
  title: string
  learnerId?: string
  tenantId?: string
  learnerStageAtGen?: number | null
  // Upload-flow Details step — labels the provisioned book.
  author?: string
  chapterLabel?: string
}

export async function createGuide(
  input: CreateGuideInput,
): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>(
    '/api/content/guides',
    input,
  )
  return data
}

export interface UpdateGuideInput {
  title?: string
  notes?: string | null
  isFavorite?: boolean
}

export async function updateGuide(
  id: string,
  patch: UpdateGuideInput,
): Promise<ReadingGuide> {
  const { data } = await apiClient.put<WireReadingGuide>(
    `/api/content/guides/${id}`,
    patch,
  )
  return normalizeGuide(data)
}

export async function deleteGuide(id: string): Promise<void> {
  await apiClient.delete(`/api/content/guides/${id}`)
}

// Persist the latest play progress for one game. Fire-and-forget from the
// reporter hook — a lost report is acceptable; a blocked game is not.
// playKey identifies one play session (one modal-open); the backend keeps a
// per-play GamePlay row for skill tracking alongside the latest-state columns.
export async function reportGameProgress(
  guideId: string,
  mechanicId: string,
  progress: { done: number; total: number; playKey?: string },
): Promise<void> {
  await apiClient.patch(
    `/api/content/guides/${guideId}/games/${mechanicId}/progress`,
    progress,
  )
}

// Pure helper — was previously co-located with the mock layer. No I/O; kept
// here so all guide-related FE logic lives in one module.
export function calculateMatch(
  guideStage: number | null,
  learnerStage: number,
): 'easy' | 'just-right' | 'stretch' {
  if (guideStage === null) return 'just-right'
  if (guideStage === learnerStage) return 'just-right'
  if (guideStage === learnerStage - 1) return 'easy'
  if (guideStage === learnerStage + 1) return 'stretch'
  if (guideStage < learnerStage - 1) return 'easy'
  return 'stretch'
}
